<script lang="ts" setup>
import { onMounted, reactive, computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  PendingFulfillmentCart,
  FulfillmentConfirmResponse,
  StuckCartsResponse,
  FulfillmentTasksResponse
} from '@/types'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { formatCurrency } from '@/utils'
import StuckCartsList from './StuckCartsList.vue'
import FulfillmentTasksList from './FulfillmentTasksList.vue'
import OrdersList from './OrdersList.vue'
import BaseDashboardPage from '@/components/base/BaseDashboardPage.vue'

const { t } = useI18n()
const notifyStore = useNotifyStore()

// Active tab
const activeTab = ref<'pending' | 'orders' | 'stuck' | 'tasks'>('pending')

const data = reactive({
  loading: false,
  carts: [] as PendingFulfillmentCart[],
  confirmingCart: null as string | null,
  // Monitoring data
  stuckCartsCount: 0,
  failedTasksCount: 0,
  threshold: ''
})

const pendingCount = computed(() => data.carts.length)

const loadPendingCarts = () => {
  data.loading = true

  useAxios<null, PendingFulfillmentCart[] | null>('GET', '/admin/fulfillment/pending', true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        // API returns null when no pending carts exist
        if (res.data === null) {
          data.carts = []
          return
        }
        const validate = PendingFulfillmentCart.array().safeParse(res.data)
        if (!validate.success) {
          console.error('Fulfillment validation failed:', validate.error)
          return
        }
        data.carts = validate.data
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'FulfillmentHome:loadPendingCarts' })
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('common.error'),
        message: getErrorMessage(error)
      })
    })
    .finally(() => {
      data.loading = false
    })
}

const confirmFulfillment = (cart: PendingFulfillmentCart) => {
  if (data.confirmingCart) return

  data.confirmingCart = cart.id

  useAxios<null, FulfillmentConfirmResponse>('POST', `/admin/fulfillment/${cart.id}/confirm`, true)
    .then((res) => {
      if (!res) return
      if (res.status === STATUS_CODES.OK) {
        const validate = FulfillmentConfirmResponse.safeParse(res.data)
        if (!validate.success) {
          console.error('Confirm response validation failed:', validate.error)
          return
        }

        const amount = formatCurrency(validate.data.amount / 100, validate.data.currency.toUpperCase(), 'de-DE')

        notifyStore.createNotify({
          id: uuid('v4'),
          alertType: 'success',
          title: 'Zahlung erfasst',
          message: `${amount} wurde erfolgreich für ${cart.account.email} erfasst`
        })

        loadPendingCarts()
      }
    })
    .catch((error) => {
      handleAxiosError(error, { logPrefix: 'FulfillmentHome:confirmFulfillment' })
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: 'error',
        title: t('common.error'),
        message: getErrorMessage(error)
      })
    })
    .finally(() => {
      data.confirmingCart = null
    })
}

const formatDate = (isoDate: string): string => {
  const date = new Date(isoDate)
  return date.toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const truncatePaymentId = (paymentId: string): string => {
  if (paymentId.length <= 20) return paymentId
  return `${paymentId.substring(0, 10)}...${paymentId.substring(paymentId.length - 6)}`
}

// Load monitoring summary (stuck carts count and failed tasks count)
const loadMonitoringSummary = async () => {
  try {
    // Fetch stuck carts count
    const stuckRes = await useAxios<null, StuckCartsResponse>(
      'GET',
      '/admin/fulfillment/stuck',
      true
    )
    if (stuckRes && stuckRes.status === STATUS_CODES.OK && stuckRes.data) {
      data.stuckCartsCount = stuckRes.data.count ?? 0
      data.threshold = stuckRes.data.threshold ?? ''
    }

    // Fetch failed tasks count
    const tasksRes = await useAxios<null, FulfillmentTasksResponse>(
      'GET',
      '/admin/fulfillment/tasks?status=failed&limit=1',
      true
    )
    if (tasksRes && tasksRes.status === STATUS_CODES.OK && tasksRes.data) {
      data.failedTasksCount = tasksRes.data.count ?? 0
    }
  } catch (err) {
    // Silent fail for monitoring summary - main functionality is pending carts
    console.error('Failed to load monitoring summary:', err)
  }
}

const handleRefresh = () => {
  loadPendingCarts()
  loadMonitoringSummary()
}

onMounted(() => {
  loadPendingCarts()
  loadMonitoringSummary()
})
</script>

<template>
  <BaseDashboardPage
    title="Fulfillment"
    subtitle="Bestellungen verwalten und Fulfillment überwachen"
    icon="mdi-cart-check"
    icon-color="orange"
  >
    <template #actions>
      <button class="refresh-btn dashboard-action-btn" @click="handleRefresh">
        <v-icon icon="mdi-refresh" size="18" />
        <span>Aktualisieren</span>
      </button>
    </template>

    <!-- Stats Pills in header-below slot -->
    <template #header-below>
      <div class="stats-container">
      <div class="stat-pill stat-pending">
        <v-icon icon="mdi-clock-outline" size="16" />
        <span class="stat-value">{{ pendingCount }}</span>
        <span class="stat-label">Ausstehend</span>
      </div>
      <div v-if="data.stuckCartsCount > 0" class="stat-pill stat-stuck">
        <v-icon icon="mdi-alert-circle" size="16" />
        <span class="stat-value">{{ data.stuckCartsCount }}</span>
        <span class="stat-label">Hängend</span>
      </div>
      <div v-if="data.failedTasksCount > 0" class="stat-pill stat-failed">
        <v-icon icon="mdi-alert-octagon" size="16" />
        <span class="stat-value">{{ data.failedTasksCount }}</span>
        <span class="stat-label">Fehlgeschlagen</span>
      </div>
      </div>
    </template>

    <!-- Tabs -->
    <div class="tabs-container">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'pending' }"
        @click="activeTab = 'pending'"
      >
        <v-icon icon="mdi-cart-check" size="18" />
        <span>Ausstehend</span>
        <span v-if="pendingCount > 0" class="tab-badge">{{ pendingCount }}</span>
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'orders' }"
        @click="activeTab = 'orders'"
      >
        <v-icon icon="mdi-package-variant-closed" size="18" />
        <span>{{ t('dashboard.admin.fulfillment.tabs.orders') }}</span>
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'stuck' }"
        @click="activeTab = 'stuck'"
      >
        <v-icon icon="mdi-cart-off" size="18" />
        <span>Hängende Carts</span>
        <span v-if="data.stuckCartsCount > 0" class="tab-badge urgent">{{ data.stuckCartsCount }}</span>
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'tasks' }"
        @click="activeTab = 'tasks'"
      >
        <v-icon icon="mdi-format-list-checks" size="18" />
        <span>Tasks</span>
      </button>
      <router-link
        :to="{ name: 'dashboardAdminWebhook' }"
        class="tab-btn tab-link"
      >
        <v-icon icon="mdi-webhook" size="18" />
        <span>Webhooks</span>
        <v-icon icon="mdi-open-in-new" size="14" class="external-icon" />
      </router-link>
    </div>

    <!-- Tab Content -->
    <div class="tab-content">
      <!-- PENDING TAB -->
      <div v-if="activeTab === 'pending'" class="table-container">
        <!-- Table Header -->
        <div class="table-header">
          <div class="header-left">
            <div class="table-icon">
              <v-icon icon="mdi-cart-check" size="20" />
            </div>
            <div class="header-text">
              <h2 class="table-title">Ausstehende Bestellungen</h2>
              <p class="table-subtitle">{{ pendingCount }} Bestellungen warten auf Fulfillment</p>
            </div>
          </div>
        </div>

      <!-- Loading State -->
      <div v-if="data.loading" class="loading-container">
        <v-progress-circular color="orange" indeterminate size="40" width="3" />
        <span class="loading-text">Lade ausstehende Bestellungen...</span>
      </div>

      <!-- Empty State -->
      <div v-else-if="data.carts.length === 0" class="empty-state">
        <div class="empty-icon">
          <v-icon icon="mdi-cart-off" size="48" />
        </div>
        <span class="empty-title">Keine ausstehenden Bestellungen</span>
        <span class="empty-subtitle">Alle Bestellungen wurden bearbeitet</span>
      </div>

      <!-- Cart Table -->
      <div v-else class="glass-table">
        <div class="table-head">
          <div class="head-cell cell-date">Datum</div>
          <div class="head-cell cell-customer">Kunde</div>
          <div class="head-cell cell-items">Artikel</div>
          <div class="head-cell cell-payment">Payment ID</div>
          <div class="head-cell cell-actions">Aktionen</div>
        </div>

        <div
          v-for="cart in data.carts"
          :key="cart.id"
          class="table-row"
        >
          <div class="row-cell cell-date">
            <span>{{ formatDate(cart.created) }}</span>
          </div>
          <div class="row-cell cell-customer">
            <span class="customer-email">{{ cart.account.email }}</span>
            <span class="customer-id">#{{ cart.account.id }}</span>
          </div>
          <div class="row-cell cell-items">
            <span
              v-for="(item, idx) in cart.items"
              :key="idx"
              class="item-badge"
            >
              {{ item.count }}x {{ item.product_name }}
            </span>
          </div>
          <div class="row-cell cell-payment">
            <span class="payment-id">{{ truncatePaymentId(cart.payment_id) }}</span>
          </div>
          <div class="row-cell cell-actions">
            <button
              class="confirm-btn"
              :disabled="data.confirmingCart === cart.id"
              @click="confirmFulfillment(cart)"
            >
              <v-progress-circular
                v-if="data.confirmingCart === cart.id"
                indeterminate
                size="16"
                width="2"
              />
              <template v-else>
                <v-icon icon="mdi-check-circle" size="18" />
                <span>Bestätigen</span>
              </template>
            </button>
          </div>
        </div>
      </div>
      </div>

      <!-- ORDERS TAB -->
      <OrdersList v-if="activeTab === 'orders'" />

      <!-- STUCK CARTS TAB -->
      <StuckCartsList
        v-if="activeTab === 'stuck'"
        @refresh="loadMonitoringSummary"
      />

      <!-- TASKS TAB -->
      <FulfillmentTasksList
        v-if="activeTab === 'tasks'"
      />
    </div>
  </BaseDashboardPage>
</template>

<style lang="scss" scoped>
.refresh-btn {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm);
  padding: var(--dashboard-btn-padding);
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.25);
  border-radius: var(--dashboard-btn-radius);
  color: #00b0ff;
  font-size: var(--dashboard-subtitle-size);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: rgba(0, 176, 255, 0.15);
    border-color: rgba(0, 176, 255, 0.35);
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
}

/* Stats Pills */
.stats-container {
  display: flex;
  flex-wrap: wrap;
  gap: var(--dashboard-gap-sm);
}

.stat-pill {
  display: flex;
  align-items: center;
  gap: var(--dashboard-gap-sm);
  padding: 8px 12px;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 24px;
  color: rgba(200, 218, 255, 0.7);

  .v-icon {
    color: rgba(200, 218, 255, 0.5);
  }

  .stat-value {
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
  }

  .stat-label {
    font-size: var(--dashboard-stat-label-size);
    color: rgba(200, 218, 255, 0.5);
  }

  &.stat-pending {
    background: rgba(255, 152, 0, 0.08);
    border-color: rgba(255, 152, 0, 0.2);

    .v-icon {
      color: #ffb74d;
    }

    .stat-value {
      color: #ffb74d;
    }
  }

  &.stat-stuck {
    background: rgba(239, 83, 80, 0.08);
    border-color: rgba(239, 83, 80, 0.2);

    .v-icon {
      color: #ef5350;
    }

    .stat-value {
      color: #ef5350;
    }
  }

  &.stat-failed {
    background: rgba(255, 193, 7, 0.08);
    border-color: rgba(255, 193, 7, 0.2);

    .v-icon {
      color: #ffc107;
    }

    .stat-value {
      color: #ffc107;
    }
  }
}

/* Tabs */
.tabs-container {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 16px;
}

.tab-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: rgba(15, 23, 48, 0.4);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 10px;
  color: rgba(200, 218, 255, 0.7);
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;

  &:hover {
    background: rgba(15, 23, 48, 0.6);
    border-color: rgba(120, 163, 255, 0.2);
    color: rgba(255, 255, 255, 0.9);
  }

  &.active {
    background: rgba(255, 152, 0, 0.1);
    border-color: rgba(255, 152, 0, 0.3);
    color: #ffb74d;
  }

  &.tab-link {
    margin-left: auto;
    background: rgba(0, 176, 255, 0.05);
    border-color: rgba(0, 176, 255, 0.15);
    color: rgba(0, 176, 255, 0.8);

    &:hover {
      background: rgba(0, 176, 255, 0.1);
      border-color: rgba(0, 176, 255, 0.25);
      color: #00b0ff;
    }
  }

  .external-icon {
    opacity: 0.6;
    margin-left: 4px;
  }
}

.tab-badge {
  padding: 2px 8px;
  background: rgba(120, 163, 255, 0.15);
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;

  &.urgent {
    background: rgba(239, 83, 80, 0.2);
    color: #ef5350;
  }
}

/* Tab Content */
.tab-content {
  background: rgba(15, 23, 48, 0.3);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 16px;
  padding: 20px;
}

/* Table Container */
.table-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.table-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.table-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(255, 152, 0, 0.1);
  border: 1px solid rgba(255, 152, 0, 0.2);
  border-radius: 12px;

  .v-icon {
    color: #ffb74d;
  }
}

.table-title {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
}

.table-subtitle {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Loading & Empty States */
.loading-container,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 300px;
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
}

.loading-text {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.6);
}

.empty-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 80px;
  height: 80px;
  background: rgba(255, 152, 0, 0.08);
  border: 1px solid rgba(255, 152, 0, 0.15);
  border-radius: 20px;

  .v-icon {
    color: rgba(255, 183, 77, 0.5);
  }
}

.empty-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
}

.empty-subtitle {
  font-size: 0.9rem;
  color: rgba(200, 218, 255, 0.5);
}

/* Glass Table */
.glass-table {
  background: rgba(15, 23, 48, 0.6);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(120, 163, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
}

.table-head {
  display: grid;
  grid-template-columns: 150px 1fr 1.5fr 180px 140px;
  padding: 14px 20px;
  background: rgba(255, 255, 255, 0.02);
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
}

.head-cell {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.cell-actions {
  text-align: center;
}

.table-row {
  display: grid;
  grid-template-columns: 150px 1fr 1.5fr 180px 140px;
  padding: 16px 20px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  transition: all 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
}

.row-cell {
  display: flex;
  align-items: center;
}

.cell-date {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.7);
}

.cell-customer {
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
}

.customer-email {
  font-size: 0.9rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.customer-id {
  font-size: 0.75rem;
  color: rgba(200, 218, 255, 0.5);
}

.cell-items {
  flex-wrap: wrap;
  gap: 6px;
}

.item-badge {
  padding: 4px 10px;
  background: rgba(0, 176, 255, 0.1);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 6px;
  font-size: 0.8rem;
  color: #00b0ff;
}

.payment-id {
  font-family: monospace;
  font-size: 0.8rem;
  color: rgba(200, 218, 255, 0.7);
}

.cell-actions {
  justify-content: center;
}

.confirm-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: rgba(76, 175, 80, 0.15);
  border: 1px solid rgba(76, 175, 80, 0.3);
  border-radius: 8px;
  color: #4caf50;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(76, 175, 80, 0.25);
    border-color: rgba(76, 175, 80, 0.45);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
}

/* Light Theme */
.v-theme--lightTheme {
  .page-title,
  .table-title {
    color: rgba(15, 23, 42, 0.95);
  }

  .page-subtitle,
  .table-subtitle {
    color: rgba(15, 23, 42, 0.6);
  }

  .stat-pill {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);

    .stat-value {
      color: rgba(15, 23, 42, 0.9);
    }

    .stat-label {
      color: rgba(15, 23, 42, 0.5);
    }
  }

  .glass-table,
  .loading-container,
  .empty-state {
    background: rgba(255, 255, 255, 0.7);
    border-color: rgba(28, 110, 227, 0.1);
  }

  .customer-email {
    color: rgba(15, 23, 42, 0.9);
  }

  .cell-date,
  .customer-id,
  .payment-id {
    color: rgba(15, 23, 42, 0.6);
  }
}

/* Responsive */
@media (max-width: 1024px) {
  .table-head,
  .table-row {
    grid-template-columns: 120px 1fr 1fr 120px;
  }

  .cell-payment {
    display: none;
  }
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .refresh-btn {
    justify-content: center;
  }

  .page-title {
    font-size: 1.5rem;
  }

  .stats-container {
    justify-content: center;
  }

  .table-head {
    display: none;
  }

  .table-row {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
  }

  .row-cell {
    width: 100%;
  }

  .cell-customer {
    flex-direction: row;
    align-items: center;
    gap: 8px;
  }

  .cell-items {
    justify-content: flex-start;
  }

  .cell-actions {
    justify-content: flex-start;
  }

  .confirm-btn {
    width: 100%;
    justify-content: center;
  }
}

@media (prefers-reduced-motion: reduce) {
  .refresh-btn,
  .table-row,
  .confirm-btn {
    transition: none;
  }

  .refresh-btn:hover,
  .confirm-btn:hover {
    transform: none;
  }
}
</style>
